import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { IIIFViewer } from "../components/IIIFViewer.js";
import { PageNav } from "../components/PageNav.js";
import { TextPanes } from "../components/TextPanes.js";
import { loadDiary, loadPage } from "../data/loader.js";
import type { DiaryWithPages, Language, PageData } from "../data/types.js";
import type { ReadingTab } from "../lib/i18n.js";
import { t } from "../lib/strings.js";
import { useMe } from "../lib/useMe.js";

interface Props {
  lang: Language;
  tab: ReadingTab;
  onTabChange: (tab: ReadingTab) => void;
}

const TITLE_PREF: Record<Language, ("de" | "en")[]> = {
  en: ["en", "de"],
  de: ["de", "en"],
  fr: ["en", "de"],
  eo: ["en", "de"],
};

function pickTitle(
  title: { de?: string; en?: string } | undefined,
  lang: Language,
  fallback: string,
): string {
  if (!title) return fallback;
  for (const key of TITLE_PREF[lang]) {
    const v = title[key];
    if (v) return v;
  }
  return fallback;
}

export function DiaryReader({ lang, tab, onTabChange }: Props): JSX.Element {
  const s = t(lang);
  const params = useParams<{ slug: string; pageNumber?: string }>();
  const slug = params.slug ?? "";
  const navigate = useNavigate();
  const { me } = useMe();

  const [diary, setDiary] = useState<DiaryWithPages | null>(null);
  const [page, setPage] = useState<PageData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pageNumber = (() => {
    const parsed = Number.parseInt(params.pageNumber ?? "1", 10);
    return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
  })();

  useEffect(() => {
    setError(null);
    loadDiary(slug)
      .then(setDiary)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [slug]);

  useEffect(() => {
    if (!diary) return;
    const clamped = Math.min(Math.max(1, pageNumber), diary.pages.length);
    if (clamped !== pageNumber) {
      navigate(`/diary/${slug}/page/${clamped}`, { replace: true });
      return;
    }
    setPage(null);
    loadPage(slug, clamped)
      .then(setPage)
      .catch((err) => setError(err instanceof Error ? err.message : String(err)));
  }, [diary, slug, pageNumber, navigate]);

  if (error) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-8 text-red-700">
        {s.errorPrefix}: {error}
      </div>
    );
  }
  if (!diary) {
    return <div className="max-w-6xl mx-auto px-4 py-8 text-ink-muted">{s.loadingDiary(0)}</div>;
  }

  const pageRef = diary.pages[pageNumber - 1];
  const title = pickTitle(diary.diary.metadata.title, lang, `Journal No. ${diary.diary.number}`);
  const manifestUrl = diary.diary.manifestUrl ?? diary.diary.metadata.manifest_url;

  const jump = (n: number) => navigate(`/diary/${slug}/page/${n}`);

  return (
    <div className="max-w-7xl mx-auto px-4 py-4 flex flex-col gap-3 lg:h-[calc(100vh-7rem)]">
      <div className="flex items-baseline justify-between gap-4">
        <h1 className="font-serif text-xl">
          {title}
          {diary.diary.dateLabel && (
            <span className="text-sm text-ink-muted ml-2">({diary.diary.dateLabel})</span>
          )}
        </h1>
        <div className="flex items-center gap-3 text-xs text-ink-muted">
          {me?.admin && (
            <Link
              to={`/admin/diary/${slug}/page/${pageNumber}`}
              className="hover:text-ink"
              title="Open this page in the admin editor"
            >
              Edit →
            </Link>
          )}
          {manifestUrl && (
            <a
              href={manifestUrl}
              target="_blank"
              rel="noreferrer"
              className="hover:text-ink"
            >
              {s.manifestLink}
            </a>
          )}
        </div>
      </div>

      <PageNav
        slug={diary.diary.slug}
        pageNumber={pageNumber}
        pageCount={diary.pages.length}
        onJump={jump}
        lang={lang}
      />

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-2 gap-3 lg:min-h-0">
        <div className="h-[60vh] lg:h-auto lg:min-h-0">
          {pageRef && (
            <IIIFViewer
              diaryId={diary.diary.onbId}
              imageId={pageRef.imageId}
              width={pageRef.width}
              height={pageRef.height}
            />
          )}
        </div>
        <div className="lg:min-h-0">
          {page ? (
            <TextPanes page={page} lang={lang} tab={tab} onTabChange={onTabChange} />
          ) : (
            <div className="bg-white border border-parchment-200 rounded-md flex items-center justify-center text-ink-muted text-sm p-8 lg:h-full">
              {s.loadingPage(pageNumber)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
